import { useEffect, useState } from 'react'
import { api, type MembershipPreview, type Portfolio, type Scope } from '../api'
import { FundPicker, useFunds } from '../components/FundPicker'
import { Checkability, Origin } from '../components/Origin'
import { says } from '../limits'

/**
 * Scope membership — TECHNICAL_DESIGN.md §5.6, §10.1.
 *
 * §10.1 keeps membership with `admin` on an operational argument: a fund is
 * redomiciled, a desk is reorganised, and gating that behind approval makes
 * routine reorganisation slow enough to be routed around. The price is that
 * adding a portfolio to a scope subjects it to every rule already there — an
 * approver-sized consequence behind an admin-sized action — and the design
 * accepts it only because of three compensating controls.
 *
 * This screen is the first of them. **The inherited set is shown before the
 * commit, and the commit sends back the identifiers that were shown.** A set that
 * moved in between is refused by the store rather than reconciled, so the
 * acknowledgement is evidence that somebody looked. The commit button does not
 * exist until the preview has been fetched — not disabled, absent, because there
 * is nothing to acknowledge yet.
 *
 * The other two controls are the store's: the audit entry names the rules, and
 * the portfolio's next attestation is brought forward. The screen reports the
 * second, because an administrator should see that they have just put work on
 * somebody's desk.
 *
 * **Several funds at once, and the control is not relaxed to allow it.** A
 * reorganisation moves a desk, not a fund, and doing it one search box at a time
 * was the shape that made somebody stop reading the inherited set by the third
 * repetition. What makes the batch safe is that `preview_join` returns *the
 * scope's* rules — `rules_attached_to_scope`, with the portfolio checked only
 * for existence — so every fund joining inherits the identical set, and one
 * reading of it is a complete reading for all of them. Each fund is still
 * committed with the identifiers that were on screen, and a refusal is reported
 * by name rather than stopping the rest.
 */
export function Membership({
  firmWide = true,
}: {
  /** Works across the whole firm. Somebody limited to desks changes only their desks' groups (decision 84). */
  firmWide?: boolean
} = {}) {
  const { funds } = useFunds()
  const [scopes, setScopes] = useState<Scope[]>([])
  const [scopeId, setScopeId] = useState('')
  const [picking, setPicking] = useState('')
  const [chosen, setChosen] = useState<Portfolio[]>([])
  const [preview, setPreview] = useState<MembershipPreview | null>(null)
  const [members, setMembers] = useState<{ portfolio_id: string; code: string; name: string }[]>([])
  /** The fund being taken out, and what it would stop being bound by. Separate
   *  from the join preview because they are opposite statements and sharing one
   *  slot is how somebody acknowledges the wrong list. */
  const [leaving, setLeaving] = useState<
    { portfolio_id: string; code: string; preview: MembershipPreview } | null
  >(null)
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .scopes()
      .then(setScopes)
      .catch((e) => setError(String(e.message ?? e)))
  }, [])

  // Changing the group changes what everything inherits, so the reading starts
  // again rather than an acknowledgement of one scope's rules carrying over to
  // another's.
  useEffect(() => {
    setChosen([])
    setPreview(null)
    setLeaving(null)
    setDone(null)
    if (!scopeId) {
      setMembers([])
      return
    }
    let live = true
    api
      .scopeMembers(scopeId)
      .then((m) => live && setMembers(m))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [scopeId])

  async function refreshMembers() {
    if (!scopeId) return
    try {
      setMembers(await api.scopeMembers(scopeId))
    } catch {
      // The list is a convenience; a stale one is not worth reporting over the
      // outcome of the change that was just made.
    }
  }

  async function lookAtLeaving(member: { portfolio_id: string; code: string }) {
    setError(null)
    setBusy(true)
    try {
      setLeaving({
        ...member,
        preview: await api.previewLeave(scopeId, member.portfolio_id),
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function confirmLeave() {
    if (!leaving) return
    setError(null)
    setBusy(true)
    try {
      // The identifiers that were on screen, exactly as joining does.
      const result = await api.endMembership(
        scopeId,
        leaving.portfolio_id,
        leaving.preview.rule_ids,
      )
      setDone(
        `${leaving.code} has left. ${leaving.preview.rules.length} rule(s) no longer ` +
          `bind it, and its next attestation has been brought forward to ` +
          `${result.attestation_due_on}.`,
      )
      setLeaving(null)
      await refreshMembers()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  function add() {
    const fund = funds.find((f) => f.id === picking)
    if (!fund || chosen.some((c) => c.id === fund.id)) return
    setChosen([...chosen, fund])
    setPicking('')
    setDone(null)
  }

  async function look() {
    if (!scopeId || chosen.length === 0) return
    setError(null)
    setBusy(true)
    try {
      // Any of them: the preview is the *scope's* rule set, so it is the same
      // for every fund joining it.
      setPreview(await api.previewJoin(scopeId, chosen[0]!.id))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function commit() {
    if (chosen.length === 0 || !preview) return
    setError(null)
    setBusy(true)
    let added = 0
    const refused: string[] = []
    const brought: string[] = []

    // One at a time and never aborting, like every other batch here: a run that
    // stopped at the first refusal would leave the administrator guessing which
    // funds are now in the group.
    for (const fund of chosen) {
      try {
        // The identifiers that were on screen, not a set fetched again at commit
        // time — that is the whole control (§10.1).
        const result = await api.addMember(scopeId, fund.id, preview.rule_ids)
        added += 1
        brought.push(`${fund.code} by ${result.attestation_due_on}`)
      } catch (e) {
        refused.push(`${fund.code}: ${e instanceof Error ? e.message : String(e)}`)
      }
    }

    setDone(
      `${added} fund(s) added. ${preview.rules.length} rule(s) now bind each of them, ` +
        `and their next attestation has been brought forward — ${brought.join('; ')}.` +
        (refused.length ? ` ${refused.length} refused — ${refused.join('; ')}` : ''),
    )
    setChosen([])
    setPreview(null)
    setBusy(false)
    await refreshMembers()
  }

  const scope = scopes.find((s) => s.id === scopeId) ?? null
  /** The firm's groups are compliance's: a desk reads who is in them and changes nothing. */
  const canChange = scope !== null && (firmWide || Boolean(scope.desk_id))

  return (
    <div className="membership">
      <header className="page-head">
        <h2>View funds in a group</h2>
        <p className="note">
          Adding a fund to a group makes every rule on that group bind it. The
          rules it is about to inherit are listed before you commit, and the list
          you were shown is what gets recorded.
        </p>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {done && <p className="done">{done}</p>}

      <div className="filters">
        <label>
          Group
          <select value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
            <option value="">Choose a group…</option>
            {scopes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.type}:{s.code} · {s.desk_code ?? 'whole firm'} — {s.portfolio_count} fund(s)
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Who is in it now. A screen that lets somebody take a fund out has to
          show which funds are in — asking the reader to remember is asking them
          to guess, on the one screen where the consequence is a limit silently
          ceasing to bind. */}
      {scope && !leaving && (
        <div className="card" data-testid="current-members">
          <h3>
            {members.length} fund(s) in {scope.code}
          </h3>
          {members.length === 0 ? (
            <p className="note">Nothing is in this group yet.</p>
          ) : (
            <ul className="chosen">
              {members.map((m) => (
                <li key={m.portfolio_id} data-testid="member">
                  <code>{m.code}</code>
                  {canChange && (
                  <button
                    className="link"
                    disabled={busy}
                    aria-label={`Take ${m.code} out of ${scope.code}`}
                    onClick={() => void lookAtLeaving(m)}
                  >
                    ×
                  </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Symmetrical with joining, deliberately: ceasing to be bound by a limit
          is the same size of consequence as becoming bound by one, and is the
          easier one to do without noticing. */}
      {leaving && (
        <section className="warning" data-testid="leave-preview">
          <h3>
            {leaving.code} would stop being bound by{' '}
            {leaving.preview.rules.length} rule(s) from {leaving.preview.scope}
          </h3>
          <p>
            Nothing is deleted. The membership closes today and the period this
            fund really was bound stays on the record — but from now on these
            limits no longer apply to it.
          </p>
          {leaving.preview.rules.length > 0 && (
            <table className="grid limits has-menus">
              <thead>
                <tr>
                  <th>Limit</th>
                  <th>Says</th>
                </tr>
              </thead>
              <tbody>
                {leaving.preview.rules.map((rule) => {
                  const said = says(rule)
                  return (
                    <tr key={rule.id} data-testid="losing-rule">
                      <td>
                        <code>{rule.rule_key}</code>
                      </td>
                      <td>
                        <span className={said.numeric ? 'limit' : 'no-number'}>
                          {said.text}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <div className="actions">
            <button className="danger" disabled={busy} onClick={() => void confirmLeave()}>
              {busy
                ? 'Removing…'
                : `Take ${leaving.code} out — I have read the ${leaving.preview.rules.length} rule(s) it loses`}
            </button>
            <button className="ghost" disabled={busy} onClick={() => setLeaving(null)}>
              Cancel
            </button>
          </div>
        </section>
      )}

      {scope && !canChange && (
        <p className="note" data-testid="firm-group">
          {scope.code} is the whole firm’s group. Compliance decides which funds are in it.
        </p>
      )}

      {scope && canChange && !leaving && (
        <div className="card">
          <h3>Add funds to {scope.code}</h3>
          <div className="row-form">
            <FundPicker
              label="Fund"
              value={picking}
              onChange={setPicking}
              funds={funds.filter(
                (f) => !chosen.some((c) => c.id === f.id) && (!scope.desk_id || f.desk_id === scope.desk_id),
              )}
              placeholder="Choose a fund…"
              testId="fund"
            />
            <button
              className="ghost"
              disabled={!picking}
              data-testid="add-fund"
              aria-label="Add this fund to the list"
              onClick={add}
            >
              + Add
            </button>
          </div>

          {chosen.length > 0 && (
            <ul className="chosen" data-testid="chosen">
              {chosen.map((fund) => (
                <li key={fund.id}>
                  <code>{fund.code}</code>
                  {fund.name !== fund.code && <span className="clause"> {fund.name}</span>}
                  <button
                    className="link"
                    aria-label={`Remove ${fund.code}`}
                    onClick={() => setChosen(chosen.filter((c) => c.id !== fund.id))}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {chosen.length > 0 && !preview && (
            <div className="actions">
              <button disabled={busy} onClick={() => void look()}>
                Show what {chosen.length === 1 ? 'it' : 'they'} would inherit
              </button>
            </div>
          )}
        </div>
      )}

      {preview && chosen.length > 0 && !leaving && (
        <section className="preview" data-testid="preview">
          <h3>
            {chosen.map((c) => c.code).join(', ')} would inherit{' '}
            {preview.rules.length} rule(s) from {preview.scope}
          </h3>
          {preview.rules.length === 0 ? (
            <p className="note">
              This group carries no rules yet, so nothing changes today — but
              these funds will inherit whatever is attached to it later.
            </p>
          ) : (
            <table className="grid limits has-menus">
              <thead>
                <tr>
                  <th>Limit</th>
                  <th>Says</th>
                  <th>From</th>
                </tr>
              </thead>
              <tbody>
                {preview.rules.map((rule) => {
                  const said = says(rule)
                  return (
                    <tr key={rule.id} data-testid="inherited-rule">
                      <td>
                        <code>{rule.rule_key}</code>
                      </td>
                      <td>
                        <span className={said.numeric ? 'limit' : 'no-number'}>
                          {said.text}
                        </span>
                        <Checkability rule={rule} />
                      </td>
                      <td>
                        <Origin rule={rule} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          {/* Present only once a preview has been seen. */}
          <div className="actions">
            <button disabled={busy} onClick={() => void commit()}>
              {busy
                ? 'Adding…'
                : `Add ${chosen.length} fund(s) — I have read the ${preview.rules.length} rule(s) above`}
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
